import { useEffect, useState, useMemo } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";

const inputCls = "w-full px-4 py-2.5 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm font-mono-spec";

function usePeriods() {
  const [periods, setPeriods] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/periods").then(({ data }) => setPeriods(data));
  }, []);

  const update = (id, field, value) => {
    setPeriods((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/periods", { periods });
      setPeriods(data);
      toast.success("Trimestres enregistrés");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return { periods, busy, update, save };
}

function PeriodsIntro() {
  return (
    <div className="pb-4 border-b border-[var(--parchment-2)]">
      <div className="eyebrow">Calendrier de comptabilisation</div>
      <h2 className="font-serif-display text-2xl font-semibold mt-1">Trimestres & semi-trimestres</h2>
      <p className="mt-2 text-sm text-[var(--ink-soft)]">
        Les absences et retards sont comptabilisés par trimestre dans les bilans et les rapports PDF.
        Les semi-trimestres servent au suivi évolutif des mercredis (rattrapages et étude).
        Dates issues du calendrier annuel officiel 2026-2027 (V1) — ajustables à tout moment.
      </p>
    </div>
  );
}

function PeriodList({ title, items, update }) {
  return (
    <>
      <div className="eyebrow !text-[var(--maroon)] mb-3">{title}</div>
      <div className="space-y-4">
        {items.map((p) => (
          <PeriodRow key={p.id} p={p} update={update} />
        ))}
      </div>
    </>
  );
}

export default function PeriodsManager() {
  const { periods, busy, update, save } = usePeriods();
  const trimestres = useMemo(() => (periods || []).filter((p) => p.kind === "trimestre"), [periods]);
  const semis = useMemo(() => (periods || []).filter((p) => p.kind === "semi"), [periods]);

  if (!periods) {
    return <div className="text-[var(--ink-soft)]">Chargement...</div>;
  }

  return (
    <div className="bg-white border border-[var(--parchment-2)] p-6" data-testid="periods-manager">
      <PeriodsIntro />
      <div className="mt-5">
        <PeriodList title="Trimestres" items={trimestres} update={update} />
        <div className="mt-8">
          <PeriodList title="Semi-trimestres (suivi des mercredis)" items={semis} update={update} />
        </div>
      </div>
      <div className="mt-6">
        <button className="btn-primary !py-2.5 !px-5 text-sm flex items-center gap-2" onClick={save} disabled={busy} data-testid="save-periods-btn">
          <Save size={15} /> Enregistrer les trimestres
        </button>
      </div>
    </div>
  );
}

function PeriodRow({ p, update }) {
  return (
    <div className="grid sm:grid-cols-3 gap-3 items-end" data-testid={`period-row-${p.key}`}>
      <div>
        <label className="eyebrow block mb-1">Nom</label>
        <input className={inputCls} value={p.name} onChange={(e) => update(p.id, "name", e.target.value)} data-testid={`period-name-${p.key}`} />
      </div>
      <div>
        <label className="eyebrow block mb-1">Début</label>
        <input className={inputCls} type="date" value={p.start} onChange={(e) => update(p.id, "start", e.target.value)} data-testid={`period-start-${p.key}`} />
      </div>
      <div>
        <label className="eyebrow block mb-1">Fin</label>
        <input className={inputCls} type="date" value={p.end} onChange={(e) => update(p.id, "end", e.target.value)} data-testid={`period-end-${p.key}`} />
      </div>
    </div>
  );
}
